import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/data/content";

const LINKS = [
  { label: "The System", href: "#system" },
  { label: "Services", href: "#services" },
  { label: "Who We Serve", href: "#serve" },
  { label: "Process", href: "#process" },
  { label: "About", href: "#about" }
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-navy/70 backdrop-blur-2xl border-b border-white/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
        >
          {/* Logo */}
          <img src={LOGO_URL} alt="Matlock Advisory Group" className={`w-auto transition-[height] duration-500 ${scrolled ? "h-12" : "h-14 sm:h-16"}`} />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="nav-link text-sm tracking-wide text-white/80 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <button
          data-testid="nav-consult-btn"
          onClick={() => go("#contact")}
          className="hidden lg:inline-flex items-center rounded-sm bg-gold px-5 py-2.5 text-sm font-medium text-navy-deep hover:bg-gold-hover transition-colors duration-300"
        >
          Schedule a Consultation
        </button>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-navy-deep/95 backdrop-blur-2xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-${l.href.replace("#", "")}`}
                  onClick={() => go(l.href)}
                  className="text-left text-lg text-white/85 py-1"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-consult-btn"
                onClick={() => go("#contact")}
                className="mt-2 rounded-sm bg-gold px-5 py-3 text-sm font-medium text-navy-deep"
              >
                Schedule a Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
